<script setup lang="ts">
/**
 * SmarticoImage - Componente de imagem otimizado para Smartico
 *
 * Features:
 * - Lazy loading nativo
 * - Skeleton placeholder enquanto carrega
 * - Prevenção de layout shift (aspect-ratio ou dimensões fixas)
 * - Fallback em caso de erro
 * - Transição suave ao carregar
 *
 * Uso:
 * <SmarticoApostatudoSmarticoImage
 *   src="https://..."
 *   alt="Mission"
 *   width="94"
 *   height="94"
 *   class="rounded-lg"
 * />
 */

interface Props {
  /** URL da imagem */
  src?: string;
  /** Texto alternativo */
  alt?: string;
  /** Largura da imagem (para prevenção de layout shift) */
  width?: string | number;
  /** Altura da imagem (para prevenção de layout shift) */
  height?: string | number;
  /** Imagem de fallback em caso de erro */
  fallback?: string;
  /** Se deve usar lazy loading (default: true) */
  lazy?: boolean;
  /** Aspect ratio (ex: "1/1", "16/9") - alternativa a width/height */
  aspectRatio?: string;
  /** Se deve mostrar skeleton enquanto carrega (default: true) */
  showSkeleton?: boolean;
  /** Duração da transição em ms (default: 300) */
  transitionDuration?: number;
  /** Como a imagem deve se ajustar: 'contain' mantém proporção, 'cover' preenche (default: 'contain') */
  objectFit?: 'contain' | 'cover';
}

const props = withDefaults(defineProps<Props>(), {
  src: '',
  alt: '',
  width: undefined,
  height: undefined,
  fallback: '',
  lazy: true,
  aspectRatio: undefined,
  showSkeleton: true,
  transitionDuration: 300,
  objectFit: 'contain',
});

const emit = defineEmits<{
  load: [event: Event];
  error: [event: Event];
}>();

// Estados
const isLoading = ref(true);
const hasError = ref(false);
const imageRef = ref<HTMLImageElement | null>(null);

// Computed para a URL atual (src ou fallback)
const currentSrc = computed(() => {
  if (hasError.value && props.fallback) {
    return props.fallback;
  }

  return props.src;
});

// Computed para estilos do container
const containerStyle = computed(() => {
  const style: Record<string, string> = {};

  if (props.aspectRatio) {
    style.aspectRatio = props.aspectRatio;
  } else {
    if (props.width) {
      style.width =
        typeof props.width === 'number' ? `${props.width}px` : props.width;
    }
    if (props.height) {
      style.height =
        typeof props.height === 'number' ? `${props.height}px` : props.height;
    }
  }

  return style;
});

// Computed para estilos da imagem (transição + object-fit)
const imageStyle = computed(() => ({
  transitionDuration: `${props.transitionDuration}ms`,
  objectFit: props.objectFit,
}));

// Handlers
const handleLoad = (event: Event) => {
  isLoading.value = false;
  emit('load', event);
};

const handleError = (event: Event) => {
  isLoading.value = false;
  hasError.value = true;
  emit('error', event);
};

// Reset quando src muda
watch(
  () => props.src,
  () => {
    if (props.src) {
      isLoading.value = true;
      hasError.value = false;
    }
  },
);

// Verificar se imagem já está em cache (carregamento instantâneo)
onMounted(() => {
  if (imageRef.value?.complete && imageRef.value?.naturalHeight > 0) {
    isLoading.value = false;
  }
});
</script>

<template>
  <div
    :class="$style.container"
    :style="containerStyle"
  >
    <!-- Skeleton -->
    <div
      v-if="showSkeleton && isLoading"
      :class="$style.skeleton"
    />

    <!-- Imagem -->
    <img
      v-if="currentSrc"
      ref="imageRef"
      :alt="alt"
      :class="[$style.image, { [$style.loaded]: !isLoading }]"
      :draggable="false"
      :loading="lazy ? 'lazy' : 'eager'"
      :src="currentSrc"
      :style="imageStyle"
      @error="handleError"
      @load="handleLoad"
    />

    <!-- Fallback visual quando não há imagem -->
    <div
      v-else-if="!currentSrc"
      :class="$style.placeholder"
    >
      <slot name="placeholder">
        <svg
          fill="none"
          height="24"
          stroke="currentColor"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="1.5"
          viewBox="0 0 24 24"
          width="24"
        >
          <rect
            height="18"
            rx="2"
            ry="2"
            width="18"
            x="3"
            y="3"
          />
          <circle
            cx="8.5"
            cy="8.5"
            r="1.5"
          />
          <polyline points="21 15 16 10 5 21" />
        </svg>
      </slot>
    </div>
  </div>
</template>

<style module lang="postcss">
.container {
  @apply relative overflow-hidden;
  /* Garante que o container mantenha as dimensões mesmo sem imagem */
  min-width: 1px;
  min-height: 1px;
}

.skeleton {
  @apply absolute inset-0;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.05) 25%,
    rgba(255, 255, 255, 0.1) 50%,
    rgba(255, 255, 255, 0.05) 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

.image {
  @apply w-full h-full;
  opacity: 0;
  transition-property: opacity;
  transition-timing-function: ease-in-out;
}

.image.loaded {
  opacity: 1;
}

.placeholder {
  @apply absolute inset-0 flex items-center justify-center;
  background: rgba(255, 255, 255, 0.05);
  color: rgba(255, 255, 255, 0.3);
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
</style>
